import { useCallback, useEffect, useState, type KeyboardEvent } from 'react'
import { useParams } from 'react-router'

import {
  isRoomCode,
  normaliseRoomCode,
  ROOM_CODE_LENGTH,
  type AvatarKey,
  type HangmanView,
  type MathsRaceView,
  type MemoryView,
} from '@hazel/shared'

import { useSession } from '../auth/session-context.js'
import { Button } from '../components/Button.js'
import { KeyLegend } from '../components/KeyLegend.js'
import { LEAVE_SHORTCUT } from '../components/shortcuts.js'
import { LeaveGame } from '../games/LeaveGame.js'
import { useGameKeys } from '../games/useGameKeys.js'
import { useLeaveOnEscape } from '../games/useLeaveOnEscape.js'
import { Card } from '../components/Card.js'
import { TeddyChoice } from '../components/TeddyChoice.js'
import { Input } from '../components/Input.js'
import { Keyboard } from '../games/Keyboard.js'
import { Milestone } from '../games/Milestone.js'
import { NumberPad } from '../games/NumberPad.js'
import { TurnSlot } from '../realtime/TurnSlot.js'
import { friendlyRoomError, type RoomPlayer } from '../realtime/types.js'
import { useRoomSocket, type SeatClaim } from '../realtime/useRoomSocket.js'

/**
 * The seat this phone holds, remembered per room.
 *
 * sessionStorage rather than localStorage: a locked phone waking up should get
 * its place back, but a fresh tab next week should not try to rejoin a game
 * that finished days ago.
 */
const SEAT_KEY_PREFIX = 'hg_seat_'

function recallSeatToken(code: string): string | null {
  try {
    return sessionStorage.getItem(SEAT_KEY_PREFIX + code)
  } catch {
    return null
  }
}

function rememberSeatToken(code: string, token: string): void {
  try {
    sessionStorage.setItem(SEAT_KEY_PREFIX + code, token)
  } catch {
    // Private browsing. Joining still works; it just cannot survive a reload.
  }
}

/**
 * Escape, for the two boxes on this screen that are typed into.
 *
 * `useGameKeys` drops every key from a text field — which is right for a
 * letter, and wrong for the one key that means "get me out of here". Join is
 * the only screen in the app where the two meet: it is the only `useGameKeys`
 * consumer with a text box in it, which is why this had never come up.
 *
 * So the box handles its own Escape, on the form rather than the window, where
 * the key still arrives. The meaning is the one it has in all seven games —
 * clear what has been typed, and leave when there is nothing left to clear —
 * so a child who has learned Escape in Stripey's Sudoku already knows what it
 * does here. Review found the legend promising a key that did nothing in the
 * exact state somebody wants it: half a code typed off the television, in the
 * wrong room.
 */
function useEscapeOut(typed: string, clear: () => void) {
  const leaveOnEscape = useLeaveOnEscape()

  return (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key !== 'Escape') return

    event.preventDefault()
    if (leaveOnEscape(typed !== '')) return

    clear()
  }
}

/**
 * What the phone is told, in a box that does not change size.
 *
 * Two lines' worth, always. These messages differ in length by a lot — "Your
 * go!" against "Bartholomew Fitzgerald is guessing…" — and a name may be
 * twenty-four characters, so the line wraps or does not depending on whose
 * turn it is. Everything below it then moves: measured on a Pixel 5, a long
 * name pushed the controls from y=212 to y=258 at the exact moment the turn
 * changed hands, which is the moment she is reaching for one.
 *
 * Two lines' worth is stated as `2.5em` against an explicit `leading-tight`,
 * rather than in pixels or in `lh`.
 *
 * Not `lh`, which says exactly this and says it better, but is Firefox 129 —
 * one version past the baseline Tailwind itself supports. An unsupported unit
 * is not an error: the declaration is simply dropped, so the jump would come
 * back silently on somebody else's phone while looking fixed here. `/join` is
 * open on purpose, and a visiting cousin's phone is whatever it is.
 *
 * Pinning the leading is what makes the `em` exact rather than a guess: two
 * lines at 1.25 is 2.5em, and both halves are written here where they cannot
 * drift apart.
 */
const TURN_SAYS =
  'font-display flex min-h-[2.5em] items-center justify-center text-center leading-tight text-(length:--text-title)'

/** Code entry, forgiving about how it was read off a television. */
function CodeEntry({ onSubmit }: { onSubmit: (code: string) => void }) {
  const [typed, setTyped] = useState('')
  const cleaned = normaliseRoomCode(typed)
  const onEscape = useEscapeOut(typed, () => setTyped(''))

  return (
    <Card>
      <form
        onSubmit={(event) => {
          event.preventDefault()
          if (isRoomCode(cleaned)) onSubmit(cleaned)
        }}
        onKeyDown={onEscape}
        className="flex flex-col gap-4"
      >
        <Input
          label="Game code"
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          hint="The four letters on the telly."
          // A code is not a word: no autocorrect, no capitalisation guessing,
          // and a keyboard that offers letters and numbers.
          autoComplete="off"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          maxLength={12}
          required
        />

        <Button type="submit" block disabled={!isRoomCode(cleaned)}>
          {cleaned.length < ROOM_CODE_LENGTH ? 'Type the code' : 'Join the game'}
        </Button>
      </form>
    </Card>
  )
}

function NameEntry({ onSubmit }: { onSubmit: (name: string, teddy: AvatarKey | null) => void }) {
  const [name, setName] = useState('')

  /*
   * Nothing chosen to begin with, and joining without choosing still works.
   *
   * A cousin who taps straight past gets exactly what they got before, which
   * is the point: this adds a choice rather than a step. Null is also what the
   * protocol has always accepted for a guest's avatar.
   */
  const [teddy, setTeddy] = useState<AvatarKey | null>(null)
  const onEscape = useEscapeOut(name, () => setName(''))

  return (
    <Card>
      <form
        onSubmit={(event) => {
          event.preventDefault()
          if (name.trim()) onSubmit(name.trim(), teddy)
        }}
        onKeyDown={onEscape}
        className="flex flex-col gap-4"
      >
        <Input
          label="What shall we call you?"
          value={name}
          onChange={(event) => setName(event.target.value)}
          hint="This is what shows up on the telly."
          autoComplete="given-name"
          maxLength={24}
          required
        />

        {/*
          A teddy as well as a name.
          
          On the television everybody with an account has their teddy and a
          guest was a plain initial in a circle. The room is the one place in
          the app where several children look at the *same* screen, so being
          able to point at yourself on it matters more here than anywhere else.

          It scrolls in its own box: twenty-five of them would push "Let me in"
          off the bottom of a phone, which is a worse problem than a list you
          have to scroll.
        */}
        <TeddyChoice
          chosen={teddy}
          onChoose={setTeddy}
          legend="Pick a teddy (if you like)"
          name="guest-teddy"
          scrolls
        />

        <Button type="submit" block disabled={!name.trim()}>
          Let me in
        </Button>
      </form>
    </Card>
  )
}

/**
 * A phone during a co-operative word game.
 *
 * Deliberately not a second screen. The word and the board are on the
 * television — duplicating them here invites everybody to look down at their
 * hands instead of up at the shared screen.
 */
function HangmanSeat({
  game,
  seatId,
  players,
  turnEndsAt,
  onGuess,
}: {
  game: HangmanView
  seatId: string
  players: readonly RoomPlayer[]
  turnEndsAt: number | null
  onGuess: (letter: string) => void
}) {
  const myTurn = game.currentPlayerId === seatId && game.status === 'playing'
  const upNow = players.find((player) => player.id === game.currentPlayerId)

  // Somebody who joined mid-round is seated but not dealt in. They are told so
  // and the keyboard is hidden entirely — a disabled one invites tapping and
  // leaves a child wondering what they broke.
  const inRound = game.roundPlayerIds.includes(seatId)

  const correct = game.guessed.filter((letter) => game.letters.includes(letter))

  return (
    <>
      <p role="status" className={TURN_SAYS}>
        {game.status !== 'playing'
          ? 'Round over — look at the telly!'
          : !inRound
            ? 'You’re in for the next one — watch the telly!'
            : myTurn
              ? 'Your go!'
              : `${upNow?.displayName ?? 'Someone'} is guessing…`}
      </p>

      <TurnSlot endsAt={turnEndsAt} mine={myTurn} />

      {game.status === 'playing' && inRound ? (
        <Keyboard guessed={game.guessed} correct={correct} disabled={!myTurn} onGuess={onGuess} />
      ) : null}
    </>
  )
}

/**
 * A phone during a memory game.
 *
 * The board is on the television, big enough to read from the sofa, so this is
 * a numbered pad rather than a second copy of it — twenty photographs on a
 * phone would be unreadable, and everybody looking down at their own board
 * defeats the point of playing it in a room together.
 *
 * The numbers match the positions on the television, counted the way you read.
 */
function MemorySeat({
  game,
  seatId,
  players,
  turnEndsAt,
  onFlip,
}: {
  game: MemoryView
  seatId: string
  players: readonly RoomPlayer[]
  turnEndsAt: number | null
  onFlip: (card: number) => void
}) {
  const myTurn = game.currentPlayerId === seatId && game.status === 'playing'
  const upNow = players.find((player) => player.id === game.currentPlayerId)
  const inRound = game.roundPlayerIds.includes(seatId)

  // Two cards are showing and everybody is meant to be looking at them. Taps
  // are refused by the engine anyway; saying so is kinder than a dead button.
  const showing = game.revealUntil !== null

  return (
    <>
      <p role="status" className={TURN_SAYS}>
        {game.status !== 'playing'
          ? 'All found — look at the telly!'
          : !inRound
            ? 'You’re in for the next one — watch the telly!'
            : showing
              ? 'Have a good look…'
              : myTurn
                ? 'Your go — pick a card!'
                : `${upNow?.displayName ?? 'Someone'} is picking…`}
      </p>

      <TurnSlot endsAt={turnEndsAt} mine={myTurn && !showing} />

      {game.status === 'playing' && inRound ? (
        <ul className="grid list-none grid-cols-5 gap-2 p-0">
          {game.cards.map((card) => (
            <li key={card.card}>
              <button
                type="button"
                // "Card 4, face down" rather than a bare number. The same
                // naming as the solo board, so the game does not describe
                // itself differently depending on which screen you are on.
                aria-label={`Card ${card.card + 1}, ${
                  card.matched ? 'found' : card.teddy ? 'turned over' : 'face down'
                }`}
                disabled={!myTurn || showing || card.matched || card.teddy !== null}
                style={{ touchAction: 'manipulation' }}
                onClick={() => onFlip(card.card)}
                className={[
                  'font-display min-h-14 w-full rounded-(--radius-control) border-2',
                  'text-(length:--text-heading) transition-colors',
                  card.matched || card.teddy
                    ? 'border-teal bg-surface-sunken text-ink-muted opacity-50'
                    : 'border-border bg-surface',
                ].join(' ')}
              >
                {card.card + 1}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </>
  )
}

/**
 * A phone during a maths race.
 *
 * This one *is* a second screen, unlike the word game: everybody is answering
 * at once, so the question has to be in her hand rather than on a television
 * eight people are sharing. The board keeps the shared part — how everyone is
 * getting on — and this keeps the part that is hers.
 *
 * Her score is here and nowhere else. The room is told how far along she is,
 * never how she is doing.
 */
function RaceSeat({ game, onAnswer }: { game: MathsRaceView; onAnswer: (value: number) => void }) {
  const [typed, setTyped] = useState('')
  const you = game.you

  function submit(): void {
    if (typed === '') return
    onAnswer(Number.parseInt(typed, 10))
    setTyped('')
  }

  // Seated, but the race had already started. Dealing her in now would put her
  // on a different question from everybody else, so she waits for the next one.
  if (!you) {
    return (
      <p role="status" className={TURN_SAYS}>
        {game.status === 'playing'
          ? 'You’re in for the next one — watch the telly!'
          : 'Round over — look at the telly!'}
      </p>
    )
  }

  if (you.finished || game.status !== 'playing') {
    return (
      <p role="status" className={TURN_SAYS}>
        {game.status !== 'playing'
          ? 'All done — look at the telly!'
          : `You’ve finished! ${you.correct} out of ${game.roundLength} right.`}
      </p>
    )
  }

  return (
    <div className="flex flex-col items-center gap-5">
      <p className="text-ink-muted text-(length:--text-small)">
        {`${you.correct} of ${game.roundLength} done`}
      </p>

      {/* One live region for the round, so the reply to the last sum cannot be
          announced after the next sum has been read out. */}
      <div className="flex flex-col items-center gap-3" aria-live="polite" aria-atomic="true">
        {you.lastAnswer ? (
          <p className={you.lastAnswer.correct ? 'text-teal' : 'text-ink-muted'}>
            {/* The same three goes as playing alone. The engine decides;
                this only says what it decided. */}
            {you.lastAnswer.correct
              ? 'Yes!'
              : you.lastAnswer.moreGoes
                ? 'Not quite — try again.'
                : `${you.lastAnswer.prompt} is ${you.lastAnswer.expected}. We’ll come back to it.`}
          </p>
        ) : null}

        <p
          className="font-display text-(length:--text-hero)"
          aria-label={`Question: ${you.prompt ?? ''}`}
        >
          {you.prompt}
        </p>
      </div>

      <p
        className="font-display border-border min-h-14 min-w-32 rounded-(--radius-control) border-2 px-4 text-(length:--text-title) tabular-nums"
        aria-label={typed === '' ? 'No answer yet' : `Your answer: ${typed}`}
      >
        {typed || ' '}
      </p>

      <NumberPad value={typed} onChange={setTyped} onSubmit={submit} />
    </div>
  )
}

function JoinFlow() {
  const params = useParams()
  const { user, status: sessionStatus } = useSession()

  const fromLink = params['code'] ? normaliseRoomCode(params['code']) : ''
  const [code, setCode] = useState(() => (isRoomCode(fromLink) ? fromLink : ''))
  const [name, setName] = useState('')

  /*
   * The teddy a guest chose, for the length of this session and no longer.
   *
   * Nothing is recorded for a guest by design — a cousin joins with a code and
   * a name and the app learns nothing about them — so this lives here rather
   * than anywhere it would outlast the room.
   */
  const [teddy, setTeddy] = useState<AvatarKey | null>(null)
  const [seatId, setSeatId] = useState<string | null>(null)

  // The seat confirmation arrives on its own event, because the token is this
  // phone's alone and must never appear in what the room is told.
  const onSeated = useCallback(
    (seat: SeatClaim) => {
      setSeatId(seat.seatId)
      if (code) rememberSeatToken(code, seat.token)
    },
    [code],
  )

  /**
   * The room has gone.
   *
   * This was written when it could not happen: a room was only ever closed
   * once nobody was connected to it, so the handler was insurance against a
   * silent hang rather than a path anybody took.
   *
   * It is a real path now (DOL-92). The television can close its room outright
   * while phones are still in it, and a room whose television has gone closes
   * ten minutes later whatever the phones are doing — so this is what a child
   * holding a phone actually gets when the room under her disappears: back to
   * the join screen, rather than a board that has quietly stopped receiving
   * anything.
   */
  const onClosed = useCallback(() => {
    setSeatId(null)
    setCode('')
  }, [])

  /**
   * A milestone this phone's player has just passed.
   *
   * Held until the next round starts rather than dismissed on a timer: she is
   * looking at the board, not at a countdown, and a message that vanishes
   * while somebody reads it out to the room is worse than one that lingers.
   */
  const [milestone, setMilestone] = useState<number | null>(null)
  const onMilestone = useCallback((games: number) => setMilestone(games), [])

  const { emit, status, room, error } = useRoomSocket({ onSeated, onClosed, onMilestone })

  const chosenName = user?.displayName ?? name

  /*
   * An account brings its own teddy; a guest brings the one they just picked.
   * Sent as null when neither applies, which is what the protocol has always
   * accepted and what every guest sent until now.
   */
  const chosenTeddy = user?.avatarKey ?? teddy

  /**
   * Claim a seat as soon as there is a code, a name and a connection.
   *
   * Driven by those three rather than by the connect event alone: a guest
   * connects first and types their name afterwards, so joining only on connect
   * left them sitting on "joining…" forever.
   *
   * Re-runs when the connection comes back, which is how a locked phone
   * reclaims its place — a remembered token keeps the same seat and turn
   * position, and without one the server seats us fresh.
   */
  useEffect(() => {
    // Never join on a half-known identity — see the session gate below.
    if (sessionStatus === 'loading') return
    if (status !== 'connected' || !code || !chosenName) return

    const token = recallSeatToken(code)
    emit('room:join', {
      code,
      displayName: chosenName,
      avatarKey: chosenTeddy,
      ...(token ? { token } : {}),
    })
  }, [status, sessionStatus, code, chosenName, chosenTeddy, emit])

  if (!code) return <CodeEntry onSubmit={setCode} />

  /**
   * Wait for the session before deciding who this is.
   *
   * `user` is null while the lookup is still in flight, so treating that as
   * "anonymous" shows a logged-in player the guest name form. On a slow phone
   * they can type a name and be seated under it — and because a reclaim keeps
   * the existing seat, the correction that arrives afterwards never lands.
   * They would stay mis-named for the whole game.
   *
   * The route guards in routes.tsx already wait for exactly this reason.
   */
  if (sessionStatus === 'loading') {
    return <p role="status">Just a moment…</p>
  }

  // A logged-in player joins as themselves; a guest picks a name first.
  if (!user && !name) {
    return (
      <div className="flex flex-col gap-4">
        <NameEntry
          onSubmit={(chosen, picked) => {
            setTeddy(picked)
            setName(chosen)
          }}
        />
        {error ? (
          <p role="alert" className="text-coral">
            {friendlyRoomError(error)}
          </p>
        ) : null}
      </div>
    )
  }

  if (!room || !seatId) {
    // A refusal here is usually a mistyped code, and a child stuck on a screen
    // with no way forward will just hand the phone to an adult.
    const canRetry = error === 'no-such-room' || error === 'room-full'

    return (
      <div className="flex flex-col gap-4">
        {error ? (
          <p role="alert" className="text-coral">
            {friendlyRoomError(error)}
          </p>
        ) : (
          <p role="status">{status === 'connected' ? 'Joining…' : 'Connecting…'}</p>
        )}

        {canRetry ? (
          <Button
            variant="secondary"
            block
            onClick={() => {
              setCode('')
              setSeatId(null)
            }}
          >
            Try a different code
          </Button>
        ) : null}
      </div>
    )
  }

  const round = room.game

  return (
    <div className="flex flex-col gap-5">
      {status === 'reconnecting' ? (
        <p role="alert" className="text-amber">
          Reconnecting…
        </p>
      ) : null}

      {milestone !== null ? <Milestone games={milestone} /> : null}

      {round === null ? (
        <p role="status" className={TURN_SAYS}>
          Waiting for the game to start…
        </p>
      ) : round.key === 'hangman' ? (
        <HangmanSeat
          game={round.view}
          seatId={seatId}
          players={room.players}
          turnEndsAt={room.turnEndsAt}
          onGuess={(letter) =>
            emit('room:guess', { code, action: { type: 'guessLetter', letter } })
          }
        />
      ) : round.key === 'memory' ? (
        <MemorySeat
          game={round.view}
          seatId={seatId}
          players={room.players}
          turnEndsAt={room.turnEndsAt}
          onFlip={(card) => emit('room:guess', { code, action: { type: 'flip', card } })}
        />
      ) : (
        <RaceSeat
          game={round.view}
          onAnswer={(value) => emit('room:guess', { code, action: { type: 'answer', value } })}
        />
      )}

      {error ? (
        <p role="alert" className="text-coral text-center">
          {friendlyRoomError(error)}
        </p>
      ) : null}
    </div>
  )
}

/**
 * A way out, in every state this screen has.
 *
 * `JoinFlow` returns early half a dozen times — no code yet, no name yet,
 * connecting, refused, seated, playing — and a phone that had typed a code had
 * no way back from any of them. Wrapping it puts the way out in all of them at
 * once, rather than in whichever five somebody remembered.
 *
 * Both a link and a key, because the two screens this runs on have different
 * things to press. `LeaveGame` is a link and hides itself on the television;
 * the legend is the opposite, hidden wherever the pointer is coarse. A phone
 * gets something to tap and a keyboard gets Escape, and neither is shown the
 * one it cannot use.
 *
 * Leaving is safe: the seat is the room's to keep, and this phone remembers
 * its token — coming back to the same code reclaims the same seat rather than
 * taking a second one.
 */
export function Join() {
  const leaveOnEscape = useLeaveOnEscape()

  useGameKeys((event) => {
    if (event.key !== 'Escape') return

    event.preventDefault()
    leaveOnEscape(false)
  })

  return (
    <div className="flex w-full flex-col items-center gap-6">
      <JoinFlow />
      <LeaveGame />
      <KeyLegend shortcuts={[LEAVE_SHORTCUT]} />
    </div>
  )
}
